import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Save, Image as ImageIcon, AlertCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * FoundersManager — admin CRUD for the /about page's founder roster.
 *
 * Per founder: photo upload / replace / clear + editable name, title, bio, LinkedIn.
 * Public read via GET /api/founders; admin writes via /api/admin/founders/{slug}.
 */
export default function FoundersManager() {
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/founders");
      setFounders(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3200);
  };

  if (loading) return <div className="text-[var(--muted)] text-sm">Loading founders…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="founders-manager">
      <div>
        <h3 className="font-display text-2xl text-[var(--navy)]">Founders</h3>
        <p className="text-sm text-[var(--muted)] mt-1">
          Manage the "Meet the Founders" section on the About page. Photos should be square
          (min 800&times;800) — JPG, PNG, or WebP, up to 8 MB.
        </p>
      </div>

      {notice && (
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 text-green-800 text-sm p-3.5" data-testid="founders-notice">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-800 text-sm p-3.5" data-testid="founders-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {founders.map((f) => (
          <FounderRow key={f.slug} founder={f} onChange={load} onFlash={flash} onError={setError} />
        ))}
      </div>
    </div>
  );
}

function FounderRow({ founder, onChange, onFlash, onError }) {
  const [form, setForm] = useState({
    name: founder.name || "",
    title: founder.title || "",
    bio: founder.bio || "",
    linkedin: founder.linkedin || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileRef = useRef(null);

  const initials = founder.name
    ? founder.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")
    : "?";

  const dirty =
    form.name !== (founder.name || "") ||
    form.title !== (founder.title || "") ||
    form.bio !== (founder.bio || "") ||
    form.linkedin !== (founder.linkedin || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/founders/${founder.slug}`, form);
      onFlash(`Saved changes for ${form.name}.`);
      await onChange();
    } catch (e) {
      onError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/founders/${founder.slug}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onFlash(`Photo uploaded for ${founder.name}.`);
      await onChange();
    } catch (err) {
      onError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearPhoto = async () => {
    if (!window.confirm(`Remove ${founder.name}'s photo? The monogram placeholder will be shown instead.`)) return;
    setClearing(true);
    try {
      await api.delete(`/admin/founders/${founder.slug}/photo`);
      onFlash(`Photo removed for ${founder.name}.`);
      await onChange();
    } catch (err) {
      onError(formatApiError(err));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="border border-[var(--line)] bg-white p-6 flex flex-col md:flex-row gap-6" data-testid={`founder-row-${founder.slug}`}>
      {/* Photo column */}
      <div className="flex-shrink-0 space-y-3 md:w-48">
        <div className="aspect-square w-full border border-[var(--line)] overflow-hidden bg-[var(--navy)] relative">
          {founder.photo_url ? (
            <img
              src={founder.photo_url}
              alt={founder.name}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`founder-photo-${founder.slug}`}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display text-[var(--cream)] text-[64px] leading-none opacity-90">
                {initials}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={upload}
            data-testid={`founder-photo-input-${founder.slug}`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn btn-navy !py-2 !px-3 !text-[11px] disabled:opacity-60 justify-center"
            data-testid={`founder-photo-upload-${founder.slug}`}
          >
            {uploading ? <><Loader2 className="animate-spin mr-2" size={12} /> Uploading…</> : <><Upload size={12} className="mr-2" /> {founder.photo_url ? "Replace" : "Upload"} Photo</>}
          </button>
          {founder.photo_url && (
            <button
              type="button"
              onClick={clearPhoto}
              disabled={clearing}
              className="btn btn-ghost !py-2 !px-3 !text-[11px] disabled:opacity-60 justify-center text-red-700"
              data-testid={`founder-photo-clear-${founder.slug}`}
            >
              {clearing ? <Loader2 className="animate-spin mr-2" size={12} /> : <Trash2 size={12} className="mr-2" />} Remove
            </button>
          )}
          {!founder.photo_url && (
            <p className="text-[10px] text-[var(--muted)] flex items-center gap-1.5">
              <ImageIcon size={11} /> Using monogram placeholder
            </p>
          )}
        </div>
      </div>

      {/* Fields column */}
      <div className="flex-1 space-y-4">
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1.5 uppercase tracking-wider">Full name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm text-[var(--ink)] focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
            data-testid={`founder-name-${founder.slug}`}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1.5 uppercase tracking-wider">Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm text-[var(--ink)] focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
            data-testid={`founder-title-${founder.slug}`}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1.5 uppercase tracking-wider">Bio</label>
          <textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm text-[var(--ink)] focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors leading-relaxed"
            data-testid={`founder-bio-${founder.slug}`}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-[var(--navy)] mb-1.5 uppercase tracking-wider">LinkedIn URL</label>
          <input
            value={form.linkedin}
            onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
            placeholder="https://www.linkedin.com/in/..."
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm text-[var(--ink)] focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
            data-testid={`founder-linkedin-${founder.slug}`}
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="btn btn-navy !py-2 !px-4 !text-[11px] disabled:opacity-60 justify-center"
            data-testid={`founder-save-${founder.slug}`}
          >
            {saving ? <><Loader2 className="animate-spin mr-2" size={12} /> Saving…</> : <><Save size={12} className="mr-2" /> Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
